import {isBefore, parseISO} from "date-fns";

import {IncidentType, StatusPageTheme} from "@/lib/types/statuspages.ts";

import type {PublicStatusPage} from "../../types.ts";
import {formatDurationRounded} from "../../utils/uptimeBarEvents.ts";
import {GlobalStatusType} from "./InspireGlobalStatus.tsx";

// The banner splits the delta into units with floor/modulo, which only holds for a positive value —
// a negative one renders as nonsense ("-34m" for a multi-day span). Browser clock skew, an incident
// whose ends_at precedes its starts_at, and a maintenance window still listed active after it ended
// can each produce one, so every branch drops the delta rather than publish it. The legacy banner
// likewise only rendered the delta when positive.
const positiveSecsOrNull = (secs: number | null | undefined): number | null => (secs != null && secs > 0 ? secs : null);

export const computeGlobalStatus = (
  statusPage: PublicStatusPage
): {status: GlobalStatusType | undefined; durationSecs: number | null} => {
  const now = new Date();
  // An open incident does not colour the banner on its own - like the legacy theme, the banner
  // follows component statuses, so an incident that moved no component leaves the page green.
  const activeMaintenance = statusPage.active_incidents.filter(
    (i) => i.incident_type === IncidentType.SCHEDULED_MAINTENANCE && isBefore(parseISO(i.starts_at), now)
  );

  if (activeMaintenance.length > 0) {
    const endsAt = activeMaintenance[0].ends_at;
    const durationSecs = endsAt ? (parseISO(endsAt).getTime() - now.getTime()) / 1000 : null;
    return {status: GlobalStatusType.MAINTENANCE, durationSecs: positiveSecsOrNull(durationSecs)};
  }

  if (!statusPage.global_is_operational) {
    // Severity wins over maintenance: a page with both a failing component and a
    // component under maintenance reports issues, not maintenance.
    if (statusPage.has_components_under_critical_state) {
      return {status: GlobalStatusType.ISSUES, durationSecs: null};
    }
    if (statusPage.has_components_under_maintenance_state) {
      return {status: GlobalStatusType.MAINTENANCE, durationSecs: null};
    }
    // Non-operational for a reason the banner has no wording for (a `notification`
    // component) — render nothing rather than claim an outage.
    return {status: undefined, durationSecs: null};
  }

  // All operational — measure from the page's last outage, whenever that was. The backend reads it
  // from whichever history the page renders, incidents counted in global metrics or raw downtimes,
  // so the banner never contradicts the bar below it (applogic.get_operational_since). It is also
  // unbounded by the visible date range, so a page whose last outage predates the window reports its
  // real uptime rather than the window length — scanning the fetched incidents could never do this,
  // since that list stops at the range the history query asked for.
  const {operational_since: operationalSince} = statusPage;
  const durationSecs = operationalSince ? (now.getTime() - parseISO(operationalSince).getTime()) / 1000 : null;

  return {status: GlobalStatusType.OPERATIONAL, durationSecs: positiveSecsOrNull(durationSecs)};
};

// The single answer to "does the card show a duration": the card renders this row, and the header
// sizes its clearance from whether it exists, because the row is what makes the card two rows tall
// below the stack breakpoint (InspireHeader .hasDurationRow). Only an operational page reports one,
// and only when the delta is positive — see positiveSecsOrNull above.
export const formatGlobalStatusDuration = (
  status: GlobalStatusType | undefined,
  durationSecs: number | null
): string | null =>
  status === GlobalStatusType.OPERATIONAL && durationSecs !== null && durationSecs > 0
    ? formatDurationRounded(durationSecs)
    : null;

export interface CustomBranding {
  headerHtml: string | undefined;
  footerHtml: string | undefined;
  css: string | undefined;
}

/**
 * Picks the custom header/footer HTML and CSS for the theme the page is saved with. LEGACY and
 * INSPIRE keep separate field sets on the model (the Django templates rendered each theme from its
 * own trio), and both themes now render through the Inspire layout, so the selection lives here.
 * Blank values collapse to undefined so callers can gate rendering on truthiness.
 */
export const getCustomBranding = (statusPage: PublicStatusPage): CustomBranding => {
  const legacy = statusPage.theme === StatusPageTheme.LEGACY;
  const pick = (value: string | null | undefined) => value?.trim() || undefined;
  return {
    headerHtml: pick(legacy ? statusPage.custom_header_html : statusPage.custom_header_html_inspire),
    footerHtml: pick(legacy ? statusPage.custom_footer_html : statusPage.custom_footer_html_inspire),
    css: pick(legacy ? statusPage.custom_css : statusPage.custom_css_inspire),
  };
};
